import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_CONTAINER_SIZE = { width: 400, height: 300 };

interface UseContainerSizeOptions {
  scale?: number;
  defaultSize?: { width: number; height: number };
}

export function useContainerSize(options: UseContainerSizeOptions = {}) {
  const { scale = 1, defaultSize = DEFAULT_CONTAINER_SIZE } = options;

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState(defaultSize);

  const getContainerSize = useCallback(() => {
    if (!containerRef.current) return defaultSize;

    const rect = containerRef.current.getBoundingClientRect();
    return {
      width: rect.width / scale,
      height: rect.height / scale,
    };
  }, [scale, defaultSize]);

  const updateContainerSize = useCallback(() => {
    const newSize = getContainerSize();
    setContainerSize(newSize);
  }, [getContainerSize]);

  useEffect(() => {
    updateContainerSize();

    const resizeObserver = new ResizeObserver(() => {
      updateContainerSize();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [updateContainerSize]);

  return {
    containerRef,
    containerSize,
    updateContainerSize,
    getContainerSize,
  };
}
